import React from 'react';
import { getTheme, useThemeMode, FONT_SERIF, TAP } from '../theme';
import { Button } from './ui';
import { userInitials } from '../userInitials';
import { ThemeSwitch } from './ThemeSwitch';
import { QidUser } from '../api';
import type { View } from '../types';
import { QButton } from '../q/QButton';

/** Huvudraden (logga | sök | konto). */
export const NAV_MAIN_H = 72;
/** Flikraden under huvudraden (Min jakt, Mer jakt, Viltkött). Döljs när bottenmenyn visas; sidorna läser höjden ur --qv-tabs-h. */
export const NAV_TABS_H = 46;
/** Sökfältet renderas av marknadsvyn hit via portal, så att sökningen behåller sin logik där. */
export const NAV_SEARCH_SLOT_ID = 'qv-nav-search-slot';

export function TopNav({
  onNav, mob, user, onLogin, cartCount, onCartOpen,
  avatarUrl, mpTabs, mpTab, onMpTab, isAdmin, onQ, onHome, hideTabs, onSettings, settingsActive,
}: {
  onNav: (v: View) => void;
  mob: boolean;
  user: QidUser | null;
  onLogin: () => void;
  cartCount: number;
  onCartOpen: () => void;
  avatarUrl?: string | null;
  hideMarketplaceCart?: boolean;
  mpTabs?: { k: string; l: string }[];
  mpTab?: string;
  onMpTab?: (k: string) => void;
  isAdmin?: boolean;
  /** Inloggad: "Q" i loggan öppnar Q (ett långt tryck börjar lyssna direkt); "venari" går alltid till startsidan. */
  onQ?: (listen: boolean) => void;
  /** Startsidan från loggan (nollställer marknadens flik). Standard: onNav('marketplace'). */
  onHome?: () => void;
  /** Inloggad: kugghjulet (Inställningar) uppe till höger bredvid profilbilden (UX1). */
  onSettings?: () => void;
  settingsActive?: boolean;
  /** Bottenmenyn visas (iOS/smal webb): flikraden (Min jakt, Mer jakt, Viltkött) ligger där i stället. */
  hideTabs?: boolean;
}) {
  const { mode } = useThemeMode();
  const T = getTheme(mode);
  const [menuOpen, setMenuOpen] = React.useState(false);
  const [mobileSearchOpen, setMobileSearchOpen] = React.useState(false);
  const menuRef = React.useRef<HTMLDivElement>(null);
  const hasSearch = !!mpTabs;

  React.useEffect(() => {
    if (!menuOpen) return;
    const h = (e: MouseEvent) => { if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [menuOpen]);

  const barBg = T.surface;
  const iconBtn: React.CSSProperties = { background: 'none', border: 'none', cursor: 'pointer', padding: 4, minWidth: TAP, minHeight: TAP, justifyContent: 'center', display: 'flex', alignItems: 'center', color: T.text, flexShrink: 0 };
  const menuItem = (color: string, weight = 400): React.CSSProperties => ({ width: '100%', display: 'block', padding: '12px 16px', minHeight: TAP, background: 'transparent', border: 'none', cursor: 'pointer', color, fontSize: 13, fontWeight: weight, fontFamily: 'inherit', textAlign: 'left' });

  const goHome = () => (onHome ? onHome() : onNav('marketplace'));
  const plainBtn: React.CSSProperties = { background: 'none', border: 'none', padding: 0, margin: 0, cursor: 'pointer', font: 'inherit', color: 'inherit' };
  const qImg = <img src="/qvenari-logo.png" alt="Q" style={{ height: mob ? 24 : 30, width: 'auto', objectFit: 'contain', flexShrink: 0 }} />;

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 800, paddingTop: 'env(safe-area-inset-top)', background: barBg }}>
      {/* ── Huvudrad: logga | sök | konto ── */}
      <nav style={{ position: 'relative', height: NAV_MAIN_H, borderBottom: `1px solid ${T.border}`, display: 'flex', alignItems: 'center', padding: mob ? '0 14px' : '0 28px', gap: 16 }}>
        {/* Logga till vänster: "Q" öppnar Q (för inloggade; långt tryck lyssnar direkt), "venari" går till startsidan */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 2, flexShrink: 0, userSelect: 'none' }}>
          {onQ ? (
            <QButton onOpen={onQ} title="Q" style={{ ...plainBtn, display: 'flex', alignItems: 'center', minHeight: TAP }}>{qImg}</QButton>
          ) : (
            <button type="button" aria-label="Qvenari – startsidan" onClick={goHome} style={{ ...plainBtn, display: 'flex', alignItems: 'center', minHeight: TAP }}>{qImg}</button>
          )}
          <button type="button" aria-label="Startsidan" onClick={goHome} style={{ ...plainBtn, minHeight: TAP, display: 'flex', alignItems: 'center' }}>
            <span style={{ fontFamily: FONT_SERIF, fontSize: mob ? 24 : 30, color: T.text, letterSpacing: '-0.5px', lineHeight: 1, marginTop: mob ? 3 : 4, whiteSpace: 'nowrap' }}>venari</span>
          </button>
        </div>

        {hasSearch && mob && (
          <button aria-label="Sök" onClick={() => setMobileSearchOpen(o => !o)} style={{ ...iconBtn, color: mobileSearchOpen ? T.amber : T.text }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>
          </button>
        )}
        {/* Sökfältet: på mobil i en rad under menyn, annars till höger om loggan */}
        {hasSearch && (
          <div
            id={NAV_SEARCH_SLOT_ID}
            style={mob
              ? { display: mobileSearchOpen ? 'block' : 'none', position: 'absolute', top: '100%', left: 0, right: 0, background: barBg, borderBottom: `1px solid ${T.border}`, padding: '10px 12px', zIndex: 2, boxShadow: '0 8px 16px rgba(0,0,0,0.06)' }
              : { width: 'min(360px, 30vw)', flexShrink: 0, position: 'relative', zIndex: 2 }}
          />
        )}

        <div style={{ flex: 1 }} />

        {/* Konto */}
        <div style={{ display: 'flex', alignItems: 'center', gap: mob ? 4 : 18, flexShrink: 0, fontSize: 14, fontWeight: 600 }}>
          {!mob && <ThemeSwitch />}
          {user && onSettings && (
            <button onClick={onSettings} title="Inställningar" aria-label="Inställningar" aria-current={settingsActive ? 'page' : undefined} style={{ ...iconBtn, color: settingsActive ? T.amber : T.text }}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 0 1-4 0v-.1a1.7 1.7 0 0 0-1.1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 0 1 0-4h.1a1.7 1.7 0 0 0 1.5-1.1 1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 0 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8V9a1.7 1.7 0 0 0 1.5 1H21a2 2 0 0 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z" /></svg>
            </button>
          )}
          {user ? (
            <button onClick={() => onNav('profil')} title="Min profil" aria-label="Min profil" style={{ ...iconBtn, width: TAP, height: TAP, justifyContent: 'center', padding: 0 }}>
              {avatarUrl
                ? <img src={avatarUrl} alt="" style={{ width: 32, height: 32, borderRadius: '50%', objectFit: 'cover' }} />
                : <span aria-hidden="true" data-testid="nav-initials" style={{ width: 32, height: 32, borderRadius: '50%', background: T.accentSoft, color: T.accentText, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, letterSpacing: '0.02em' }}>{userInitials(user)}</span>}
            </button>
          ) : (
            <Button variant="ghost" size="md" onClick={onLogin} style={{ padding: mob ? '0 8px' : '0 14px', fontSize: mob ? 13 : 14 }}>Logga in</Button>
          )}
          {onMpTab && !mob && (
            <button onClick={() => onMpTab('arrende')} style={{ padding: '10px 16px', borderRadius: 999, border: `1px solid ${T.border}`, background: 'transparent', color: T.text, fontSize: 14, fontWeight: 600, fontFamily: 'inherit', cursor: 'pointer', whiteSpace: 'nowrap' }}>Erbjud jakt</button>
          )}

          {/* Meny: mina sidor, admin, jaktkorg */}
          {(mob || user || isAdmin || cartCount > 0) && <div ref={menuRef} style={{ position: 'relative' }}>
            <button aria-label="Meny" onClick={() => setMenuOpen(o => !o)} style={{ ...iconBtn, position: 'relative', color: menuOpen ? T.amber : T.text }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><line x1="3" y1="6" x2="21" y2="6" /><line x1="3" y1="12" x2="21" y2="12" /><line x1="3" y1="18" x2="21" y2="18" /></svg>
              {cartCount > 0 && <span style={{ position: 'absolute', top: -4, right: -6, background: T.accentFill, color: T.onAccent, borderRadius: '50%', fontSize: 10, fontWeight: 700, padding: '0 4px', lineHeight: '15px', minWidth: 15, textAlign: 'center' }}>{cartCount}</span>}
            </button>
            {menuOpen && (
              <div style={{ position: 'absolute', right: 0, top: 'calc(100% + 10px)', minWidth: 200, background: T.surface, border: `1px solid ${T.border}`, borderRadius: 6, zIndex: 1000, padding: '6px 0', boxShadow: '0 8px 24px rgba(0,0,0,0.12)' }}>
                {mob && <>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 16px 8px' }}><ThemeSwitch color={T.text} /></div>
                  <div style={{ height: 1, background: T.border, margin: '0 0 4px' }} />
                </>}
                {mob && onMpTab && <button onClick={() => { onMpTab('arrende'); setMenuOpen(false); }} style={menuItem(T.text, 600)}>Erbjud jakt</button>}
                {user && <>
                  <div style={{ color: T.textMuted, fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', padding: '4px 16px 2px' }}>Mina sidor</div>
                  {(['profil', 'dashboard', 'avskjutning'] as const).map(k => {
                    const labels: Record<string, string> = { profil: 'Jägarprofil', dashboard: 'Dashboard', avskjutning: 'Avskjutning' };
                    return <button key={k} onClick={() => { onNav(k); setMenuOpen(false); }} style={menuItem(T.text)} onMouseEnter={e => (e.currentTarget.style.background = T.amberDim)} onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>{labels[k]}</button>;
                  })}
                </>}
                {isAdmin && <>
                  <div style={{ height: 1, background: T.border, margin: '4px 0' }} />
                  <div style={{ color: T.textMuted, fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', padding: '4px 16px 2px' }}>Admin</div>
                  <button onClick={() => { onNav('admin'); setMenuOpen(false); }} style={menuItem(T.amber, 500)} onMouseEnter={e => (e.currentTarget.style.background = T.amberDim)} onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>Adminsida</button>
                  <button onClick={() => { onNav('jaktlag'); setMenuOpen(false); }} style={menuItem(T.amber, 500)} onMouseEnter={e => (e.currentTarget.style.background = T.amberDim)} onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>Jaktlag</button>
                </>}
                {cartCount > 0 && <>
                  <div style={{ height: 1, background: T.border, margin: '4px 0' }} />
                  <button onClick={() => { onCartOpen(); setMenuOpen(false); }} style={menuItem(T.amber, 600)} onMouseEnter={e => (e.currentTarget.style.background = T.amberDim)} onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>Jaktkorg ({cartCount})</button>
                </>}
              </div>
            )}
          </div>}
        </div>
      </nav>

      {/* ── Flikrad (Qvestiums kategorirad) ── */}
      {mpTabs && mpTabs.length > 0 && onMpTab && !hideTabs && (
        <div style={{ height: NAV_TABS_H, borderBottom: `1px solid ${T.border}`, padding: mob ? '0 14px' : '0 28px' }}>
          <div className="hide-scrollbar" style={{ height: '100%', display: 'flex', alignItems: 'stretch', gap: mob ? 22 : 30, overflowX: 'auto', scrollbarWidth: 'none' }}>
            {mpTabs.map(({ k, l }) => {
              const active = mpTab === k;
              return (
                <button key={k} onClick={() => onMpTab(k)} style={{ background: 'none', border: 'none', borderBottom: `2px solid ${active ? T.amber : 'transparent'}`, padding: '0 0 1px', marginBottom: -1, cursor: 'pointer', fontFamily: 'inherit', fontSize: 13, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: active ? T.text : T.textSub, whiteSpace: 'nowrap', flexShrink: 0 }}>{l}</button>
              );
            })}
          </div>
        </div>
      )}
      <style>{`.hide-scrollbar::-webkit-scrollbar{display:none}`}</style>
    </div>
  );
}
